/* @generated from adl module ferovinum.app.config */

import * as ADL from './../../runtime/adl';
import * as common_config_aws from './../../common/config/aws';
import * as common_config_db from './../../common/config/db';
import * as common_config_flyway from './../../common/config/flyway';
import * as common_config_frontend from './../../common/config/frontend';
import * as common_config_log from './../../common/config/log';
import * as common_strings from './../../common/strings';

/**
 * Configuration for the server
 */
export interface ServerConfig {
  /**
   * Port to listen for requests
   */
  port: number;
  /**
   * Environment name, e.g. dev, uat, prod
   */
  environment: string;
  /**
   * Name of the release / code version
   */
  releaseName: string;
  /**
   * The shared secret that will be present on requests
   * made by the cron webhook system.
   */
  cronSharedSecret: string;
  /**
   * The shared secret that will be present on requests from the queue
   * processor system
   */
  processorSecret: string;
  /**
   * Secret used to sign JWT tokens
   */
  jwtSecret: string;
  /**
   * Database config
   */
  db: common_config_db.PostgreSqlConfig;
  /**
   * Server-side logging config
   */
  logging: common_config_log.LogConfig;
  /**
   * Client-side logging config
   */
  clientLogging: ClientLogConfig;
  /**
   * Frontend threadpool configuration
   */
  threadPool: common_config_frontend.ThreadPoolConfig;
  flyway: (common_config_flyway.FlywayConfig|null);
  secretUrls: SecretUrls;
  awsRegion: common_config_aws.AwsRegionProvider;
  emailNotificationsConfig: EmailNotificationsConfig;
  trueLayerApi: TrueLayerApiConfig;
  visionApis: VisionApiConfig[];
  xeroApi: XeroApiConfig;
  blobStoreConfig: BlobStoreConfig;
  localBlobStoreConfig: (LocalBlobStoreConfig|null);
  freightTransportApi: FreightTransportApiConfig;
  hotjarConfig: HotjarConfig;
  /**
   * The app url for the current environment
   */
  appBaseUrl: string;
  useLocalstack: boolean;
  localstackHost: string;
  /**
   * The queues that are used to broadcast messages to the Fero ecosystem and
   * to receive feedback from the various services.
   */
  messageQueueConfig: MessageQueueConfig;
  /**
   * The domain of the accounting service to use
   */
  accountingServiceDomain: string;
  purchaseRequestTimelineDurations: PurchaseRequestTimelineDurations;
  amsterdamWarehouseCompany: AmsterdamWarehouseCompany;
  /**
   * Indicates whether the instance is a reporting instance
   */
  isReportingInstance: boolean;
  reportingStoreConfig: ReportingStoreConfig;
  apiDeckApi: ApiDeckApiConfig;
  shipEngineApi: ShipEngineApiConfig;
  docuSignApi: DocuSignApiConfig;
  emailIntelligenceConfig: EmailIntelligenceConfig;
  /**
   * The hostname of the node server. Value should be null if one should not be started 
   */
  nodeServerHostname: string;
  /**
   * The restate config
   */
  restateConfig: RestateConfig;
  /**
   * Secret used to authenticate internal API calls between Java and Node appserver
   */
  internalApiSecret: string;
  /**
   * Leave this as the last entry please.
   */
  devConfig: DevConfig;
  httpRateLimitDefault: number;
  httpRateLimitHighFrequent: number;
  /**
   * The domain of the API eg. https://api.fero.capital
   */
  apiDomainUrl: string;
  corsAllowedOrigins: string[];
}

export function makeServerConfig(
  input: {
    port: number,
    environment: string,
    releaseName: string,
    cronSharedSecret: string,
    processorSecret: string,
    jwtSecret: string,
    db: common_config_db.PostgreSqlConfig,
    logging: common_config_log.LogConfig,
    clientLogging?: ClientLogConfig,
    threadPool: common_config_frontend.ThreadPoolConfig,
    flyway?: (common_config_flyway.FlywayConfig|null),
    secretUrls: SecretUrls,
    awsRegion: common_config_aws.AwsRegionProvider,
    emailNotificationsConfig: EmailNotificationsConfig,
    trueLayerApi: TrueLayerApiConfig,
    visionApis: VisionApiConfig[],
    xeroApi: XeroApiConfig,
    blobStoreConfig: BlobStoreConfig,
    localBlobStoreConfig: (LocalBlobStoreConfig|null),
    freightTransportApi: FreightTransportApiConfig,
    hotjarConfig: HotjarConfig,
    appBaseUrl: string,
    useLocalstack?: boolean,
    localstackHost?: string,
    messageQueueConfig: MessageQueueConfig,
    accountingServiceDomain: string,
    purchaseRequestTimelineDurations: PurchaseRequestTimelineDurations,
    amsterdamWarehouseCompany: AmsterdamWarehouseCompany,
    isReportingInstance: boolean,
    reportingStoreConfig: ReportingStoreConfig,
    apiDeckApi: ApiDeckApiConfig,
    shipEngineApi: ShipEngineApiConfig,
    docuSignApi: DocuSignApiConfig,
    emailIntelligenceConfig: EmailIntelligenceConfig,
    nodeServerHostname: string,
    restateConfig: RestateConfig,
    internalApiSecret: string,
    devConfig: DevConfig,
    httpRateLimitDefault: number,
    httpRateLimitHighFrequent: number,
    apiDomainUrl: string,
    corsAllowedOrigins: string[],
  }
): ServerConfig {
  return {
    port: input.port,
    environment: input.environment,
    releaseName: input.releaseName,
    cronSharedSecret: input.cronSharedSecret,
    processorSecret: input.processorSecret,
    jwtSecret: input.jwtSecret,
    db: input.db,
    logging: input.logging,
    clientLogging: input.clientLogging === undefined ? null : input.clientLogging,
    threadPool: input.threadPool,
    flyway: input.flyway === undefined ? {dbMigrationDir : "/app/sql/migrations", autoBaseline : false} : input.flyway,
    secretUrls: input.secretUrls,
    awsRegion: input.awsRegion,
    emailNotificationsConfig: input.emailNotificationsConfig,
    trueLayerApi: input.trueLayerApi,
    visionApis: input.visionApis,
    xeroApi: input.xeroApi,
    blobStoreConfig: input.blobStoreConfig,
    localBlobStoreConfig: input.localBlobStoreConfig,
    freightTransportApi: input.freightTransportApi,
    hotjarConfig: input.hotjarConfig,
    appBaseUrl: input.appBaseUrl,
    useLocalstack: input.useLocalstack === undefined ? false : input.useLocalstack,
    localstackHost: input.localstackHost === undefined ? "http://localstack:4566" : input.localstackHost,
    messageQueueConfig: input.messageQueueConfig,
    accountingServiceDomain: input.accountingServiceDomain,
    purchaseRequestTimelineDurations: input.purchaseRequestTimelineDurations,
    amsterdamWarehouseCompany: input.amsterdamWarehouseCompany,
    isReportingInstance: input.isReportingInstance,
    reportingStoreConfig: input.reportingStoreConfig,
    apiDeckApi: input.apiDeckApi,
    shipEngineApi: input.shipEngineApi,
    docuSignApi: input.docuSignApi,
    emailIntelligenceConfig: input.emailIntelligenceConfig,
    nodeServerHostname: input.nodeServerHostname,
    restateConfig: input.restateConfig,
    internalApiSecret: input.internalApiSecret,
    devConfig: input.devConfig,
    httpRateLimitDefault: input.httpRateLimitDefault,
    httpRateLimitHighFrequent: input.httpRateLimitHighFrequent,
    apiDomainUrl: input.apiDomainUrl,
    corsAllowedOrigins: input.corsAllowedOrigins,
  };
}

const ServerConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[{"value":"Configuration for the server\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[{"value":"Port to listen for requests\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"port","default":{"kind":"nothing"},"name":"port","typeExpr":{"typeRef":{"kind":"primitive","value":"Int16"},"parameters":[]}},{"annotations":[{"value":"Environment name, e.g. dev, uat, prod\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"environment","default":{"kind":"nothing"},"name":"environment","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Name of the release / code version\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"releaseName","default":{"kind":"nothing"},"name":"releaseName","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"The shared secret that will be present on requests\nmade by the cron webhook system.\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"cronSharedSecret","default":{"kind":"nothing"},"name":"cronSharedSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"The shared secret that will be present on requests from the queue\nprocessor system\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"processorSecret","default":{"kind":"nothing"},"name":"processorSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Secret used to sign JWT tokens\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"jwtSecret","default":{"kind":"nothing"},"name":"jwtSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Database config\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"db","default":{"kind":"nothing"},"name":"db","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.db","name":"PostgreSqlConfig"}},"parameters":[]}},{"annotations":[{"value":"Server-side logging config\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"logging","default":{"kind":"nothing"},"name":"logging","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.log","name":"LogConfig"}},"parameters":[]}},{"annotations":[{"value":"Client-side logging config\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"clientLogging","default":{"kind":"just","value":null},"name":"clientLogging","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ClientLogConfig"}},"parameters":[]}},{"annotations":[{"value":"Frontend threadpool configuration\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"threadPool","default":{"kind":"nothing"},"name":"threadPool","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.frontend","name":"ThreadPoolConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"flyway","default":{"kind":"just","value":{}},"name":"flyway","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.flyway","name":"FlywayConfig"}},"parameters":[]}]}},{"annotations":[],"serializedName":"secretUrls","default":{"kind":"nothing"},"name":"secretUrls","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"SecretUrls"}},"parameters":[]}},{"annotations":[],"serializedName":"awsRegion","default":{"kind":"nothing"},"name":"awsRegion","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.aws","name":"AwsRegionProvider"}},"parameters":[]}},{"annotations":[],"serializedName":"emailNotificationsConfig","default":{"kind":"nothing"},"name":"emailNotificationsConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"EmailNotificationsConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"trueLayerApi","default":{"kind":"nothing"},"name":"trueLayerApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"TrueLayerApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"visionApis","default":{"kind":"nothing"},"name":"visionApis","typeExpr":{"typeRef":{"kind":"primitive","value":"Vector"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"VisionApiConfig"}},"parameters":[]}]}},{"annotations":[],"serializedName":"xeroApi","default":{"kind":"nothing"},"name":"xeroApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"XeroApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"blobStoreConfig","default":{"kind":"nothing"},"name":"blobStoreConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"BlobStoreConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"localBlobStoreConfig","default":{"kind":"nothing"},"name":"localBlobStoreConfig","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"LocalBlobStoreConfig"}},"parameters":[]}]}},{"annotations":[],"serializedName":"freightTransportApi","default":{"kind":"nothing"},"name":"freightTransportApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"FreightTransportApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"hotjarConfig","default":{"kind":"nothing"},"name":"hotjarConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"HotjarConfig"}},"parameters":[]}},{"annotations":[{"value":"The app url for the current environment\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"appBaseUrl","default":{"kind":"nothing"},"name":"appBaseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"useLocalstack","default":{"kind":"just","value":false},"name":"useLocalstack","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"localstackHost","default":{"kind":"just","value":"http://localstack:4566"},"name":"localstackHost","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"The queues that are used to broadcast messages to the Fero ecosystem and\nto receive feedback from the various services.\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"messageQueueConfig","default":{"kind":"nothing"},"name":"messageQueueConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"MessageQueueConfig"}},"parameters":[]}},{"annotations":[{"value":"The domain of the accounting service to use\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"accountingServiceDomain","default":{"kind":"nothing"},"name":"accountingServiceDomain","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"purchaseRequestTimelineDurations","default":{"kind":"nothing"},"name":"purchaseRequestTimelineDurations","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"PurchaseRequestTimelineDurations"}},"parameters":[]}},{"annotations":[],"serializedName":"amsterdamWarehouseCompany","default":{"kind":"nothing"},"name":"amsterdamWarehouseCompany","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"AmsterdamWarehouseCompany"}},"parameters":[]}},{"annotations":[{"value":"Indicates whether the instance is a reporting instance\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"isReportingInstance","default":{"kind":"nothing"},"name":"isReportingInstance","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"reportingStoreConfig","default":{"kind":"nothing"},"name":"reportingStoreConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ReportingStoreConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"apiDeckApi","default":{"kind":"nothing"},"name":"apiDeckApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ApiDeckApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"shipEngineApi","default":{"kind":"nothing"},"name":"shipEngineApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ShipEngineApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"docuSignApi","default":{"kind":"nothing"},"name":"docuSignApi","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"DocuSignApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"emailIntelligenceConfig","default":{"kind":"nothing"},"name":"emailIntelligenceConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"EmailIntelligenceConfig"}},"parameters":[]}},{"annotations":[{"value":"The hostname of the node server. Value should be null if one should not be started \n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"nodeServerHostname","default":{"kind":"nothing"},"name":"nodeServerHostname","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"The restate config\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"restateConfig","default":{"kind":"nothing"},"name":"restateConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"RestateConfig"}},"parameters":[]}},{"annotations":[{"value":"Secret used to authenticate internal API calls between Java and Node appserver\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"internalApiSecret","default":{"kind":"nothing"},"name":"internalApiSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Leave this as the last entry please.\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"devConfig","default":{"kind":"nothing"},"name":"devConfig","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"DevConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"httpRateLimitDefault","default":{"kind":"nothing"},"name":"httpRateLimitDefault","typeExpr":{"typeRef":{"kind":"primitive","value":"Int16"},"parameters":[]}},{"annotations":[],"serializedName":"httpRateLimitHighFrequent","default":{"kind":"nothing"},"name":"httpRateLimitHighFrequent","typeExpr":{"typeRef":{"kind":"primitive","value":"Int16"},"parameters":[]}},{"annotations":[{"value":"The domain of the API eg. https://api.fero.capital\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"apiDomainUrl","default":{"kind":"nothing"},"name":"apiDomainUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"corsAllowedOrigins","default":{"kind":"nothing"},"name":"corsAllowedOrigins","typeExpr":{"typeRef":{"kind":"primitive","value":"Vector"},"parameters":[{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}]}}]}},"name":"ServerConfig","version":{"kind":"nothing"}}};

export const snServerConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ServerConfig"};

export function texprServerConfig(): ADL.ATypeExpr<ServerConfig> {
  return {value : {typeRef : {kind: "reference", value : snServerConfig}, parameters : []}};
}

export interface DevConfig {
  logJavlin: boolean;
  writeDevEmailToLog: boolean;
  writeDevEmailToFile: boolean;
  blobStoreConfig: (BlobStoreConfig|null);
  localSimulatedS3Config: (S3BlobStoreConfig|null);
}

export function makeDevConfig(
  input: {
    logJavlin?: boolean,
    writeDevEmailToLog?: boolean,
    writeDevEmailToFile?: boolean,
    blobStoreConfig: (BlobStoreConfig|null),
    localSimulatedS3Config: (S3BlobStoreConfig|null),
  }
): DevConfig {
  return {
    logJavlin: input.logJavlin === undefined ? true : input.logJavlin,
    writeDevEmailToLog: input.writeDevEmailToLog === undefined ? true : input.writeDevEmailToLog,
    writeDevEmailToFile: input.writeDevEmailToFile === undefined ? false : input.writeDevEmailToFile,
    blobStoreConfig: input.blobStoreConfig,
    localSimulatedS3Config: input.localSimulatedS3Config,
  };
}

const DevConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"logJavlin","default":{"kind":"just","value":true},"name":"logJavlin","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"writeDevEmailToLog","default":{"kind":"just","value":true},"name":"writeDevEmailToLog","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"writeDevEmailToFile","default":{"kind":"just","value":false},"name":"writeDevEmailToFile","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"blobStoreConfig","default":{"kind":"nothing"},"name":"blobStoreConfig","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"BlobStoreConfig"}},"parameters":[]}]}},{"annotations":[],"serializedName":"localSimulatedS3Config","default":{"kind":"nothing"},"name":"localSimulatedS3Config","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"S3BlobStoreConfig"}},"parameters":[]}]}}]}},"name":"DevConfig","version":{"kind":"nothing"}}};

export const snDevConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"DevConfig"};

export function texprDevConfig(): ADL.ATypeExpr<DevConfig> {
  return {value : {typeRef : {kind: "reference", value : snDevConfig}, parameters : []}};
}

export interface MessageQueueConfig {
  saleOrdersQueue: string;
  saleOrdersFeedbackQueue: string;
  negativeStockAdjustmentQueue: string;
  negativeStockAdjustmentFeedbackQueue: string;
  newStockProcurementFeedbackDepositInvoicedQueue: string;
  existingStockProcurementQueue: string;
  newDealRequestLossesQueue: string;
  newDealRequestLossesFeedbackQueue: string;
  voidNewDealInvoiceQueue: string;
  voidNewDealInvoiceFeedbackQueue: string;
  newStockProcurementQueue: string;
  dealRollsQueue: string;
  dealRollsFeedbackQueue: string;
  supplierIsPaidQueue: string;
  thirdPartySalesQueue: string;
  thirdPartySalesFeedbackQueue: string;
  extendedPayloadS3BucketName: string;
  newStockProcurementFeedbackPurchaseBillCreatedQueue: string;
  thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue: string;
  docuSignConnectQueue: string;
  lawPurchaseRequestReadyForCollectionQueue: string;
  purchaseRequestCsvReportQueue: string;
}

export function makeMessageQueueConfig(
  input: {
    saleOrdersQueue: string,
    saleOrdersFeedbackQueue: string,
    negativeStockAdjustmentQueue: string,
    negativeStockAdjustmentFeedbackQueue: string,
    newStockProcurementFeedbackDepositInvoicedQueue: string,
    existingStockProcurementQueue: string,
    newDealRequestLossesQueue: string,
    newDealRequestLossesFeedbackQueue: string,
    voidNewDealInvoiceQueue: string,
    voidNewDealInvoiceFeedbackQueue: string,
    newStockProcurementQueue: string,
    dealRollsQueue: string,
    dealRollsFeedbackQueue: string,
    supplierIsPaidQueue: string,
    thirdPartySalesQueue: string,
    thirdPartySalesFeedbackQueue: string,
    extendedPayloadS3BucketName: string,
    newStockProcurementFeedbackPurchaseBillCreatedQueue: string,
    thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue: string,
    docuSignConnectQueue: string,
    lawPurchaseRequestReadyForCollectionQueue: string,
    purchaseRequestCsvReportQueue: string,
  }
): MessageQueueConfig {
  return {
    saleOrdersQueue: input.saleOrdersQueue,
    saleOrdersFeedbackQueue: input.saleOrdersFeedbackQueue,
    negativeStockAdjustmentQueue: input.negativeStockAdjustmentQueue,
    negativeStockAdjustmentFeedbackQueue: input.negativeStockAdjustmentFeedbackQueue,
    newStockProcurementFeedbackDepositInvoicedQueue: input.newStockProcurementFeedbackDepositInvoicedQueue,
    existingStockProcurementQueue: input.existingStockProcurementQueue,
    newDealRequestLossesQueue: input.newDealRequestLossesQueue,
    newDealRequestLossesFeedbackQueue: input.newDealRequestLossesFeedbackQueue,
    voidNewDealInvoiceQueue: input.voidNewDealInvoiceQueue,
    voidNewDealInvoiceFeedbackQueue: input.voidNewDealInvoiceFeedbackQueue,
    newStockProcurementQueue: input.newStockProcurementQueue,
    dealRollsQueue: input.dealRollsQueue,
    dealRollsFeedbackQueue: input.dealRollsFeedbackQueue,
    supplierIsPaidQueue: input.supplierIsPaidQueue,
    thirdPartySalesQueue: input.thirdPartySalesQueue,
    thirdPartySalesFeedbackQueue: input.thirdPartySalesFeedbackQueue,
    extendedPayloadS3BucketName: input.extendedPayloadS3BucketName,
    newStockProcurementFeedbackPurchaseBillCreatedQueue: input.newStockProcurementFeedbackPurchaseBillCreatedQueue,
    thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue: input.thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue,
    docuSignConnectQueue: input.docuSignConnectQueue,
    lawPurchaseRequestReadyForCollectionQueue: input.lawPurchaseRequestReadyForCollectionQueue,
    purchaseRequestCsvReportQueue: input.purchaseRequestCsvReportQueue,
  };
}

const MessageQueueConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"saleOrdersQueue","default":{"kind":"nothing"},"name":"saleOrdersQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"saleOrdersFeedbackQueue","default":{"kind":"nothing"},"name":"saleOrdersFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"negativeStockAdjustmentQueue","default":{"kind":"nothing"},"name":"negativeStockAdjustmentQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"negativeStockAdjustmentFeedbackQueue","default":{"kind":"nothing"},"name":"negativeStockAdjustmentFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"newStockProcurementFeedbackDepositInvoicedQueue","default":{"kind":"nothing"},"name":"newStockProcurementFeedbackDepositInvoicedQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"existingStockProcurementQueue","default":{"kind":"nothing"},"name":"existingStockProcurementQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"newDealRequestLossesQueue","default":{"kind":"nothing"},"name":"newDealRequestLossesQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"newDealRequestLossesFeedbackQueue","default":{"kind":"nothing"},"name":"newDealRequestLossesFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"voidNewDealInvoiceQueue","default":{"kind":"nothing"},"name":"voidNewDealInvoiceQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"voidNewDealInvoiceFeedbackQueue","default":{"kind":"nothing"},"name":"voidNewDealInvoiceFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"newStockProcurementQueue","default":{"kind":"nothing"},"name":"newStockProcurementQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"dealRollsQueue","default":{"kind":"nothing"},"name":"dealRollsQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"dealRollsFeedbackQueue","default":{"kind":"nothing"},"name":"dealRollsFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"supplierIsPaidQueue","default":{"kind":"nothing"},"name":"supplierIsPaidQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"thirdPartySalesQueue","default":{"kind":"nothing"},"name":"thirdPartySalesQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"thirdPartySalesFeedbackQueue","default":{"kind":"nothing"},"name":"thirdPartySalesFeedbackQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"extendedPayloadS3BucketName","default":{"kind":"nothing"},"name":"extendedPayloadS3BucketName","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"newStockProcurementFeedbackPurchaseBillCreatedQueue","default":{"kind":"nothing"},"name":"newStockProcurementFeedbackPurchaseBillCreatedQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue","default":{"kind":"nothing"},"name":"thirdPartySalesAdvanceUponDeliveryIsPaidSqsQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"docuSignConnectQueue","default":{"kind":"nothing"},"name":"docuSignConnectQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"lawPurchaseRequestReadyForCollectionQueue","default":{"kind":"nothing"},"name":"lawPurchaseRequestReadyForCollectionQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"purchaseRequestCsvReportQueue","default":{"kind":"nothing"},"name":"purchaseRequestCsvReportQueue","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"MessageQueueConfig","version":{"kind":"nothing"}}};

export const snMessageQueueConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"MessageQueueConfig"};

export function texprMessageQueueConfig(): ADL.ATypeExpr<MessageQueueConfig> {
  return {value : {typeRef : {kind: "reference", value : snMessageQueueConfig}, parameters : []}};
}

export type ClientLogConfig = (common_config_log.FluentdConfig|null);

const ClientLogConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"type_","value":{"typeParams":[],"typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.log","name":"FluentdConfig"}},"parameters":[]}]}}},"name":"ClientLogConfig","version":{"kind":"nothing"}}};

export const snClientLogConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ClientLogConfig"};

export function texprClientLogConfig(): ADL.ATypeExpr<ClientLogConfig> {
  return {value : {typeRef : {kind: "reference", value : snClientLogConfig}, parameters : []}};
}

export interface OAuthConfig {
  clientId: string;
  clientSecret: string;
  tokenServerUrl: string;
  authorisationServerUrl: (string|null);
  redirectUri: (string|null);
}

export function makeOAuthConfig(
  input: {
    clientId: string,
    clientSecret: string,
    tokenServerUrl: string,
    authorisationServerUrl?: (string|null),
    redirectUri?: (string|null),
  }
): OAuthConfig {
  return {
    clientId: input.clientId,
    clientSecret: input.clientSecret,
    tokenServerUrl: input.tokenServerUrl,
    authorisationServerUrl: input.authorisationServerUrl === undefined ? null : input.authorisationServerUrl,
    redirectUri: input.redirectUri === undefined ? null : input.redirectUri,
  };
}

const OAuthConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"clientId","default":{"kind":"nothing"},"name":"clientId","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"clientSecret","default":{"kind":"nothing"},"name":"clientSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"tokenServerUrl","default":{"kind":"nothing"},"name":"tokenServerUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"authorisationServerUrl","default":{"kind":"just","value":null},"name":"authorisationServerUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}]}},{"annotations":[],"serializedName":"redirectUri","default":{"kind":"just","value":null},"name":"redirectUri","typeExpr":{"typeRef":{"kind":"primitive","value":"Nullable"},"parameters":[{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}]}}]}},"name":"OAuthConfig","version":{"kind":"nothing"}}};

export const snOAuthConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"OAuthConfig"};

export function texprOAuthConfig(): ADL.ATypeExpr<OAuthConfig> {
  return {value : {typeRef : {kind: "reference", value : snOAuthConfig}, parameters : []}};
}

export interface EmailNotificationsConfig {
  enabled: boolean;
  fromEmail: common_strings.EmailAddress;
  accountingFromEmail: common_strings.EmailAddress;
  toEmail: common_strings.EmailAddress;
}

export function makeEmailNotificationsConfig(
  input: {
    enabled: boolean,
    fromEmail: common_strings.EmailAddress,
    accountingFromEmail: common_strings.EmailAddress,
    toEmail: common_strings.EmailAddress,
  }
): EmailNotificationsConfig {
  return {
    enabled: input.enabled,
    fromEmail: input.fromEmail,
    accountingFromEmail: input.accountingFromEmail,
    toEmail: input.toEmail,
  };
}

const EmailNotificationsConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"enabled","default":{"kind":"nothing"},"name":"enabled","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"fromEmail","default":{"kind":"nothing"},"name":"fromEmail","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.strings","name":"EmailAddress"}},"parameters":[]}},{"annotations":[],"serializedName":"accountingFromEmail","default":{"kind":"nothing"},"name":"accountingFromEmail","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.strings","name":"EmailAddress"}},"parameters":[]}},{"annotations":[],"serializedName":"toEmail","default":{"kind":"nothing"},"name":"toEmail","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.strings","name":"EmailAddress"}},"parameters":[]}}]}},"name":"EmailNotificationsConfig","version":{"kind":"nothing"}}};

export const snEmailNotificationsConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"EmailNotificationsConfig"};

export function texprEmailNotificationsConfig(): ADL.ATypeExpr<EmailNotificationsConfig> {
  return {value : {typeRef : {kind: "reference", value : snEmailNotificationsConfig}, parameters : []}};
}

export interface RestateConfig {
  invokeUrl: string;
  apiKey: string;
}

export function makeRestateConfig(
  input: {
    invokeUrl: string,
    apiKey: string,
  }
): RestateConfig {
  return {
    invokeUrl: input.invokeUrl,
    apiKey: input.apiKey,
  };
}

const RestateConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"invokeUrl","default":{"kind":"nothing"},"name":"invokeUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"apiKey","default":{"kind":"nothing"},"name":"apiKey","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"RestateConfig","version":{"kind":"nothing"}}};

export const snRestateConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"RestateConfig"};

export function texprRestateConfig(): ADL.ATypeExpr<RestateConfig> {
  return {value : {typeRef : {kind: "reference", value : snRestateConfig}, parameters : []}};
}

export interface SecretUrls {
  prefix: string;
  viewPurchasedStock: string;
  viewPurchasedStockCSV: string;
  viewSoldStock: string;
  viewSoldStockCSV: string;
  viewCapitalisedExpenses: string;
  viewCapitalisedExpensesCSV: string;
}

export function makeSecretUrls(
  input: {
    prefix: string,
    viewPurchasedStock?: string,
    viewPurchasedStockCSV?: string,
    viewSoldStock?: string,
    viewSoldStockCSV?: string,
    viewCapitalisedExpenses?: string,
    viewCapitalisedExpensesCSV?: string,
  }
): SecretUrls {
  return {
    prefix: input.prefix,
    viewPurchasedStock: input.viewPurchasedStock === undefined ? "viewPurchasedStock" : input.viewPurchasedStock,
    viewPurchasedStockCSV: input.viewPurchasedStockCSV === undefined ? "viewPurchasedStockCSV" : input.viewPurchasedStockCSV,
    viewSoldStock: input.viewSoldStock === undefined ? "viewSoldStock" : input.viewSoldStock,
    viewSoldStockCSV: input.viewSoldStockCSV === undefined ? "viewSoldStockCSV" : input.viewSoldStockCSV,
    viewCapitalisedExpenses: input.viewCapitalisedExpenses === undefined ? "viewCapitalisedExpenses" : input.viewCapitalisedExpenses,
    viewCapitalisedExpensesCSV: input.viewCapitalisedExpensesCSV === undefined ? "viewCapitalisedExpensesCSV" : input.viewCapitalisedExpensesCSV,
  };
}

const SecretUrls_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"prefix","default":{"kind":"nothing"},"name":"prefix","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewPurchasedStock","default":{"kind":"just","value":"viewPurchasedStock"},"name":"viewPurchasedStock","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewPurchasedStockCSV","default":{"kind":"just","value":"viewPurchasedStockCSV"},"name":"viewPurchasedStockCSV","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewSoldStock","default":{"kind":"just","value":"viewSoldStock"},"name":"viewSoldStock","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewSoldStockCSV","default":{"kind":"just","value":"viewSoldStockCSV"},"name":"viewSoldStockCSV","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewCapitalisedExpenses","default":{"kind":"just","value":"viewCapitalisedExpenses"},"name":"viewCapitalisedExpenses","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"viewCapitalisedExpensesCSV","default":{"kind":"just","value":"viewCapitalisedExpensesCSV"},"name":"viewCapitalisedExpensesCSV","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"SecretUrls","version":{"kind":"nothing"}}};

export const snSecretUrls: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"SecretUrls"};

export function texprSecretUrls(): ADL.ATypeExpr<SecretUrls> {
  return {value : {typeRef : {kind: "reference", value : snSecretUrls}, parameters : []}};
}

export interface VisionApiConfig {
  baseUrl: string;
  name: string;
  oAuth: OAuthConfig;
}

export function makeVisionApiConfig(
  input: {
    baseUrl: string,
    name: string,
    oAuth: OAuthConfig,
  }
): VisionApiConfig {
  return {
    baseUrl: input.baseUrl,
    name: input.name,
    oAuth: input.oAuth,
  };
}

const VisionApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"name","default":{"kind":"nothing"},"name":"name","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"oAuth","default":{"kind":"nothing"},"name":"oAuth","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"OAuthConfig"}},"parameters":[]}}]}},"name":"VisionApiConfig","version":{"kind":"nothing"}}};

export const snVisionApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"VisionApiConfig"};

export function texprVisionApiConfig(): ADL.ATypeExpr<VisionApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snVisionApiConfig}, parameters : []}};
}

export interface XeroApiConfig {
  baseUrl: string;
  name: string;
  oAuth: OAuthConfig;
}

export function makeXeroApiConfig(
  input: {
    baseUrl: string,
    name: string,
    oAuth: OAuthConfig,
  }
): XeroApiConfig {
  return {
    baseUrl: input.baseUrl,
    name: input.name,
    oAuth: input.oAuth,
  };
}

const XeroApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"name","default":{"kind":"nothing"},"name":"name","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"oAuth","default":{"kind":"nothing"},"name":"oAuth","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"OAuthConfig"}},"parameters":[]}}]}},"name":"XeroApiConfig","version":{"kind":"nothing"}}};

export const snXeroApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"XeroApiConfig"};

export function texprXeroApiConfig(): ADL.ATypeExpr<XeroApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snXeroApiConfig}, parameters : []}};
}

export interface TrueLayerApiConfig {
  baseUrl: string;
  oAuth: OAuthConfig;
}

export function makeTrueLayerApiConfig(
  input: {
    baseUrl: string,
    oAuth: OAuthConfig,
  }
): TrueLayerApiConfig {
  return {
    baseUrl: input.baseUrl,
    oAuth: input.oAuth,
  };
}

const TrueLayerApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"oAuth","default":{"kind":"nothing"},"name":"oAuth","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"OAuthConfig"}},"parameters":[]}}]}},"name":"TrueLayerApiConfig","version":{"kind":"nothing"}}};

export const snTrueLayerApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"TrueLayerApiConfig"};

export function texprTrueLayerApiConfig(): ADL.ATypeExpr<TrueLayerApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snTrueLayerApiConfig}, parameters : []}};
}

export interface BlobStoreConfig_S3 {
  kind: 's3';
  value: S3BlobStoreConfig;
}
export interface BlobStoreConfig_Localstack {
  kind: 'localstack';
  value: S3BlobStoreConfig;
}
export interface BlobStoreConfig_Local {
  kind: 'local';
  value: S3LocalBlobStoreConfig;
}

export type BlobStoreConfig = BlobStoreConfig_S3 | BlobStoreConfig_Localstack | BlobStoreConfig_Local;

export interface BlobStoreConfigOpts {
  s3: S3BlobStoreConfig;
  localstack: S3BlobStoreConfig;
  local: S3LocalBlobStoreConfig;
}

export function makeBlobStoreConfig<K extends keyof BlobStoreConfigOpts>(kind: K, value: BlobStoreConfigOpts[K]) { return {kind, value}; }

const BlobStoreConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"union_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"s3","default":{"kind":"nothing"},"name":"s3","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"S3BlobStoreConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"localstack","default":{"kind":"nothing"},"name":"localstack","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"S3BlobStoreConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"local","default":{"kind":"nothing"},"name":"local","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"S3LocalBlobStoreConfig"}},"parameters":[]}}]}},"name":"BlobStoreConfig","version":{"kind":"nothing"}}};

export const snBlobStoreConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"BlobStoreConfig"};

export function texprBlobStoreConfig(): ADL.ATypeExpr<BlobStoreConfig> {
  return {value : {typeRef : {kind: "reference", value : snBlobStoreConfig}, parameters : []}};
}

export interface S3LocalBlobStoreConfig {
  filePath: string;
  simulatedS3Config: S3BlobStoreConfig;
}

export function makeS3LocalBlobStoreConfig(
  input: {
    filePath: string,
    simulatedS3Config: S3BlobStoreConfig,
  }
): S3LocalBlobStoreConfig {
  return {
    filePath: input.filePath,
    simulatedS3Config: input.simulatedS3Config,
  };
}

const S3LocalBlobStoreConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"filePath","default":{"kind":"nothing"},"name":"filePath","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"simulatedS3Config","default":{"kind":"nothing"},"name":"simulatedS3Config","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"S3BlobStoreConfig"}},"parameters":[]}}]}},"name":"S3LocalBlobStoreConfig","version":{"kind":"nothing"}}};

export const snS3LocalBlobStoreConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"S3LocalBlobStoreConfig"};

export function texprS3LocalBlobStoreConfig(): ADL.ATypeExpr<S3LocalBlobStoreConfig> {
  return {value : {typeRef : {kind: "reference", value : snS3LocalBlobStoreConfig}, parameters : []}};
}

export interface LocalBlobStoreConfig {
  filePath: string;
}

export function makeLocalBlobStoreConfig(
  input: {
    filePath: string,
  }
): LocalBlobStoreConfig {
  return {
    filePath: input.filePath,
  };
}

const LocalBlobStoreConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"filePath","default":{"kind":"nothing"},"name":"filePath","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"LocalBlobStoreConfig","version":{"kind":"nothing"}}};

export const snLocalBlobStoreConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"LocalBlobStoreConfig"};

export function texprLocalBlobStoreConfig(): ADL.ATypeExpr<LocalBlobStoreConfig> {
  return {value : {typeRef : {kind: "reference", value : snLocalBlobStoreConfig}, parameters : []}};
}

export interface S3BlobStoreConfig {
  assetLocation: common_config_aws.S3Location;
  documentLocation: common_config_aws.S3Location;
}

export function makeS3BlobStoreConfig(
  input: {
    assetLocation: common_config_aws.S3Location,
    documentLocation: common_config_aws.S3Location,
  }
): S3BlobStoreConfig {
  return {
    assetLocation: input.assetLocation,
    documentLocation: input.documentLocation,
  };
}

const S3BlobStoreConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"assetLocation","default":{"kind":"nothing"},"name":"assetLocation","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.aws","name":"S3Location"}},"parameters":[]}},{"annotations":[],"serializedName":"documentLocation","default":{"kind":"nothing"},"name":"documentLocation","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.aws","name":"S3Location"}},"parameters":[]}}]}},"name":"S3BlobStoreConfig","version":{"kind":"nothing"}}};

export const snS3BlobStoreConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"S3BlobStoreConfig"};

export function texprS3BlobStoreConfig(): ADL.ATypeExpr<S3BlobStoreConfig> {
  return {value : {typeRef : {kind: "reference", value : snS3BlobStoreConfig}, parameters : []}};
}

export interface HotjarConfig {
  siteId: string;
  version: string;
}

export function makeHotjarConfig(
  input: {
    siteId: string,
    version: string,
  }
): HotjarConfig {
  return {
    siteId: input.siteId,
    version: input.version,
  };
}

const HotjarConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"siteId","default":{"kind":"nothing"},"name":"siteId","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"version","default":{"kind":"nothing"},"name":"version","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"HotjarConfig","version":{"kind":"nothing"}}};

export const snHotjarConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"HotjarConfig"};

export function texprHotjarConfig(): ADL.ATypeExpr<HotjarConfig> {
  return {value : {typeRef : {kind: "reference", value : snHotjarConfig}, parameters : []}};
}

export interface PurchaseRequestTimelineDurations {
  maxCollectionDays: number;
}

export function makePurchaseRequestTimelineDurations(
  input: {
    maxCollectionDays?: number,
  }
): PurchaseRequestTimelineDurations {
  return {
    maxCollectionDays: input.maxCollectionDays === undefined ? 14 : input.maxCollectionDays,
  };
}

const PurchaseRequestTimelineDurations_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"maxCollectionDays","default":{"kind":"just","value":14},"name":"maxCollectionDays","typeExpr":{"typeRef":{"kind":"primitive","value":"Int16"},"parameters":[]}}]}},"name":"PurchaseRequestTimelineDurations","version":{"kind":"nothing"}}};

export const snPurchaseRequestTimelineDurations: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"PurchaseRequestTimelineDurations"};

export function texprPurchaseRequestTimelineDurations(): ADL.ATypeExpr<PurchaseRequestTimelineDurations> {
  return {value : {typeRef : {kind: "reference", value : snPurchaseRequestTimelineDurations}, parameters : []}};
}

export interface AmsterdamWarehouseCompany {
  saleOrderEmail: string;
  feroContactEmail: string;
  feroContactNumber: string;
}

export function makeAmsterdamWarehouseCompany(
  input: {
    saleOrderEmail: string,
    feroContactEmail: string,
    feroContactNumber: string,
  }
): AmsterdamWarehouseCompany {
  return {
    saleOrderEmail: input.saleOrderEmail,
    feroContactEmail: input.feroContactEmail,
    feroContactNumber: input.feroContactNumber,
  };
}

const AmsterdamWarehouseCompany_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"saleOrderEmail","default":{"kind":"nothing"},"name":"saleOrderEmail","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"feroContactEmail","default":{"kind":"nothing"},"name":"feroContactEmail","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"feroContactNumber","default":{"kind":"nothing"},"name":"feroContactNumber","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"AmsterdamWarehouseCompany","version":{"kind":"nothing"}}};

export const snAmsterdamWarehouseCompany: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"AmsterdamWarehouseCompany"};

export function texprAmsterdamWarehouseCompany(): ADL.ATypeExpr<AmsterdamWarehouseCompany> {
  return {value : {typeRef : {kind: "reference", value : snAmsterdamWarehouseCompany}, parameters : []}};
}

export interface ReportingStoreConfig_Local {
  kind: 'local';
  value: string;
}
export interface ReportingStoreConfig_S3 {
  kind: 's3';
  value: common_config_aws.S3Location;
}

export type ReportingStoreConfig = ReportingStoreConfig_Local | ReportingStoreConfig_S3;

export interface ReportingStoreConfigOpts {
  local: string;
  s3: common_config_aws.S3Location;
}

export function makeReportingStoreConfig<K extends keyof ReportingStoreConfigOpts>(kind: K, value: ReportingStoreConfigOpts[K]) { return {kind, value}; }

const ReportingStoreConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"union_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"local","default":{"kind":"nothing"},"name":"local","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"s3","default":{"kind":"nothing"},"name":"s3","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.aws","name":"S3Location"}},"parameters":[]}}]}},"name":"ReportingStoreConfig","version":{"kind":"nothing"}}};

export const snReportingStoreConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ReportingStoreConfig"};

export function texprReportingStoreConfig(): ADL.ATypeExpr<ReportingStoreConfig> {
  return {value : {typeRef : {kind: "reference", value : snReportingStoreConfig}, parameters : []}};
}

export interface ApiDeckApiConfig {
  baseUrl: string;
  apiKey: string;
  appId: string;
}

export function makeApiDeckApiConfig(
  input: {
    baseUrl: string,
    apiKey: string,
    appId: string,
  }
): ApiDeckApiConfig {
  return {
    baseUrl: input.baseUrl,
    apiKey: input.apiKey,
    appId: input.appId,
  };
}

const ApiDeckApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"apiKey","default":{"kind":"nothing"},"name":"apiKey","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"appId","default":{"kind":"nothing"},"name":"appId","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"ApiDeckApiConfig","version":{"kind":"nothing"}}};

export const snApiDeckApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ApiDeckApiConfig"};

export function texprApiDeckApiConfig(): ADL.ATypeExpr<ApiDeckApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snApiDeckApiConfig}, parameters : []}};
}

export interface FreightTransportApiConfig {
  baseUrl: string;
  apiKey: string;
  ftlCarrierId: string;
}

export function makeFreightTransportApiConfig(
  input: {
    baseUrl: string,
    apiKey: string,
    ftlCarrierId: string,
  }
): FreightTransportApiConfig {
  return {
    baseUrl: input.baseUrl,
    apiKey: input.apiKey,
    ftlCarrierId: input.ftlCarrierId,
  };
}

const FreightTransportApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"apiKey","default":{"kind":"nothing"},"name":"apiKey","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"ftlCarrierId","default":{"kind":"nothing"},"name":"ftlCarrierId","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"FreightTransportApiConfig","version":{"kind":"nothing"}}};

export const snFreightTransportApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"FreightTransportApiConfig"};

export function texprFreightTransportApiConfig(): ADL.ATypeExpr<FreightTransportApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snFreightTransportApiConfig}, parameters : []}};
}

export interface ShipEngineApiConfig {
  baseUrl: string;
  apiKey: string;
}

export function makeShipEngineApiConfig(
  input: {
    baseUrl: string,
    apiKey: string,
  }
): ShipEngineApiConfig {
  return {
    baseUrl: input.baseUrl,
    apiKey: input.apiKey,
  };
}

const ShipEngineApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"apiKey","default":{"kind":"nothing"},"name":"apiKey","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}]}},"name":"ShipEngineApiConfig","version":{"kind":"nothing"}}};

export const snShipEngineApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ShipEngineApiConfig"};

export function texprShipEngineApiConfig(): ADL.ATypeExpr<ShipEngineApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snShipEngineApiConfig}, parameters : []}};
}

export interface DocuSignApiConfig {
  baseUrl: string;
  rsaPrivateKey: string;
  userId: string;
  oAuth: OAuthConfig;
}

export function makeDocuSignApiConfig(
  input: {
    baseUrl: string,
    rsaPrivateKey: string,
    userId: string,
    oAuth: OAuthConfig,
  }
): DocuSignApiConfig {
  return {
    baseUrl: input.baseUrl,
    rsaPrivateKey: input.rsaPrivateKey,
    userId: input.userId,
    oAuth: input.oAuth,
  };
}

const DocuSignApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"baseUrl","default":{"kind":"nothing"},"name":"baseUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"rsaPrivateKey","default":{"kind":"nothing"},"name":"rsaPrivateKey","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"userId","default":{"kind":"nothing"},"name":"userId","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"oAuth","default":{"kind":"nothing"},"name":"oAuth","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"OAuthConfig"}},"parameters":[]}}]}},"name":"DocuSignApiConfig","version":{"kind":"nothing"}}};

export const snDocuSignApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"DocuSignApiConfig"};

export function texprDocuSignApiConfig(): ADL.ATypeExpr<DocuSignApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snDocuSignApiConfig}, parameters : []}};
}

export interface EmailIntelligenceConfig {
  enable: boolean;
  server: string;
  port: number;
}

export function makeEmailIntelligenceConfig(
  input: {
    enable: boolean,
    server: string,
    port: number,
  }
): EmailIntelligenceConfig {
  return {
    enable: input.enable,
    server: input.server,
    port: input.port,
  };
}

const EmailIntelligenceConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"enable","default":{"kind":"nothing"},"name":"enable","typeExpr":{"typeRef":{"kind":"primitive","value":"Bool"},"parameters":[]}},{"annotations":[],"serializedName":"server","default":{"kind":"nothing"},"name":"server","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"port","default":{"kind":"nothing"},"name":"port","typeExpr":{"typeRef":{"kind":"primitive","value":"Int32"},"parameters":[]}}]}},"name":"EmailIntelligenceConfig","version":{"kind":"nothing"}}};

export const snEmailIntelligenceConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"EmailIntelligenceConfig"};

export function texprEmailIntelligenceConfig(): ADL.ATypeExpr<EmailIntelligenceConfig> {
  return {value : {typeRef : {kind: "reference", value : snEmailIntelligenceConfig}, parameters : []}};
}

export type ApiConfigName = string;

const ApiConfigName_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"type_","value":{"typeParams":[],"typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}}},"name":"ApiConfigName","version":{"kind":"nothing"}}};

export const snApiConfigName: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ApiConfigName"};

export function texprApiConfigName(): ADL.ATypeExpr<ApiConfigName> {
  return {value : {typeRef : {kind: "reference", value : snApiConfigName}, parameters : []}};
}

export interface ApiConfigValue_ApiDeck {
  kind: 'apiDeck';
  value: ApiDeckApiConfig;
}
export interface ApiConfigValue_FreightTransport {
  kind: 'freightTransport';
  value: FreightTransportApiConfig;
}
export interface ApiConfigValue_ShipEngine {
  kind: 'shipEngine';
  value: ShipEngineApiConfig;
}
export interface ApiConfigValue_DocuSign {
  kind: 'docuSign';
  value: DocuSignApiConfig;
}

export type ApiConfigValue = ApiConfigValue_ApiDeck | ApiConfigValue_FreightTransport | ApiConfigValue_ShipEngine | ApiConfigValue_DocuSign;

export interface ApiConfigValueOpts {
  apiDeck: ApiDeckApiConfig;
  freightTransport: FreightTransportApiConfig;
  shipEngine: ShipEngineApiConfig;
  docuSign: DocuSignApiConfig;
}

export function makeApiConfigValue<K extends keyof ApiConfigValueOpts>(kind: K, value: ApiConfigValueOpts[K]) { return {kind, value}; }

const ApiConfigValue_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"union_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"apiDeck","default":{"kind":"nothing"},"name":"apiDeck","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ApiDeckApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"freightTransport","default":{"kind":"nothing"},"name":"freightTransport","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"FreightTransportApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"shipEngine","default":{"kind":"nothing"},"name":"shipEngine","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ShipEngineApiConfig"}},"parameters":[]}},{"annotations":[],"serializedName":"docuSign","default":{"kind":"nothing"},"name":"docuSign","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"DocuSignApiConfig"}},"parameters":[]}}]}},"name":"ApiConfigValue","version":{"kind":"nothing"}}};

export const snApiConfigValue: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ApiConfigValue"};

export function texprApiConfigValue(): ADL.ATypeExpr<ApiConfigValue> {
  return {value : {typeRef : {kind: "reference", value : snApiConfigValue}, parameters : []}};
}

export interface ApiConfig {
  name: ApiConfigName;
  value: ApiConfigValue;
}

export function makeApiConfig(
  input: {
    name: ApiConfigName,
    value: ApiConfigValue,
  }
): ApiConfig {
  return {
    name: input.name,
    value: input.value,
  };
}

const ApiConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"name","default":{"kind":"nothing"},"name":"name","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ApiConfigName"}},"parameters":[]}},{"annotations":[],"serializedName":"value","default":{"kind":"nothing"},"name":"value","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"ApiConfigValue"}},"parameters":[]}}]}},"name":"ApiConfig","version":{"kind":"nothing"}}};

export const snApiConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"ApiConfig"};

export function texprApiConfig(): ADL.ATypeExpr<ApiConfig> {
  return {value : {typeRef : {kind: "reference", value : snApiConfig}, parameters : []}};
}

/**
 * Config file that is passed to the node server
 */
export interface NodeServerConfig {
  port: number;
  environment: string;
  releaseName: string;
  /**
   * Server-side logging config
   */
  logging: common_config_log.LogLevel;
  /**
   * Format of the logs emitted from the server
   */
  logType: LogType;
  db: common_config_db.PostgreSqlConfig;
  /**
   * Secret used to sign JWT tokens
   */
  jwtSecret: string;
  /**
   * Secret used to authenticate internal API calls between Java and Node appserver
   */
  internalApiSecret: string;
  otelGrpcUrl: string;
  slackNotificationToken: string;
  corsAllowedOrigins: string[];
}

export function makeNodeServerConfig(
  input: {
    port: number,
    environment: string,
    releaseName: string,
    logging: common_config_log.LogLevel,
    logType?: LogType,
    db: common_config_db.PostgreSqlConfig,
    jwtSecret: string,
    internalApiSecret: string,
    otelGrpcUrl: string,
    slackNotificationToken: string,
    corsAllowedOrigins: string[],
  }
): NodeServerConfig {
  return {
    port: input.port,
    environment: input.environment,
    releaseName: input.releaseName,
    logging: input.logging,
    logType: input.logType === undefined ? "text" : input.logType,
    db: input.db,
    jwtSecret: input.jwtSecret,
    internalApiSecret: input.internalApiSecret,
    otelGrpcUrl: input.otelGrpcUrl,
    slackNotificationToken: input.slackNotificationToken,
    corsAllowedOrigins: input.corsAllowedOrigins,
  };
}

const NodeServerConfig_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[{"value":"Config file that is passed to the node server\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"type_":{"kind":"struct_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"port","default":{"kind":"nothing"},"name":"port","typeExpr":{"typeRef":{"kind":"primitive","value":"Int32"},"parameters":[]}},{"annotations":[],"serializedName":"environment","default":{"kind":"nothing"},"name":"environment","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"releaseName","default":{"kind":"nothing"},"name":"releaseName","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Server-side logging config\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"logging","default":{"kind":"nothing"},"name":"logging","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.log","name":"LogLevel"}},"parameters":[]}},{"annotations":[{"value":"Format of the logs emitted from the server\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"logType","default":{"kind":"just","value":"text"},"name":"logType","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"ferovinum.app.config","name":"LogType"}},"parameters":[]}},{"annotations":[],"serializedName":"db","default":{"kind":"nothing"},"name":"db","typeExpr":{"typeRef":{"kind":"reference","value":{"moduleName":"common.config.db","name":"PostgreSqlConfig"}},"parameters":[]}},{"annotations":[{"value":"Secret used to sign JWT tokens\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"jwtSecret","default":{"kind":"nothing"},"name":"jwtSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[{"value":"Secret used to authenticate internal API calls between Java and Node appserver\n","key":{"moduleName":"sys.annotations","name":"Doc"}}],"serializedName":"internalApiSecret","default":{"kind":"nothing"},"name":"internalApiSecret","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"otelGrpcUrl","default":{"kind":"nothing"},"name":"otelGrpcUrl","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"slackNotificationToken","default":{"kind":"nothing"},"name":"slackNotificationToken","typeExpr":{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}},{"annotations":[],"serializedName":"corsAllowedOrigins","default":{"kind":"nothing"},"name":"corsAllowedOrigins","typeExpr":{"typeRef":{"kind":"primitive","value":"Vector"},"parameters":[{"typeRef":{"kind":"primitive","value":"String"},"parameters":[]}]}}]}},"name":"NodeServerConfig","version":{"kind":"nothing"}}};

export const snNodeServerConfig: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"NodeServerConfig"};

export function texprNodeServerConfig(): ADL.ATypeExpr<NodeServerConfig> {
  return {value : {typeRef : {kind: "reference", value : snNodeServerConfig}, parameters : []}};
}

export type LogType = 'text' | 'json';
export const valuesLogType : LogType[] = ['text', 'json'];

const LogType_AST : ADL.ScopedDecl =
  {"moduleName":"ferovinum.app.config","decl":{"annotations":[],"type_":{"kind":"union_","value":{"typeParams":[],"fields":[{"annotations":[],"serializedName":"text","default":{"kind":"nothing"},"name":"text","typeExpr":{"typeRef":{"kind":"primitive","value":"Void"},"parameters":[]}},{"annotations":[],"serializedName":"json","default":{"kind":"nothing"},"name":"json","typeExpr":{"typeRef":{"kind":"primitive","value":"Void"},"parameters":[]}}]}},"name":"LogType","version":{"kind":"nothing"}}};

export const snLogType: ADL.ScopedName = {moduleName:"ferovinum.app.config", name:"LogType"};

export function texprLogType(): ADL.ATypeExpr<LogType> {
  return {value : {typeRef : {kind: "reference", value : snLogType}, parameters : []}};
}

export const _AST_MAP: { [key: string]: ADL.ScopedDecl } = {
  "ferovinum.app.config.ServerConfig" : ServerConfig_AST,
  "ferovinum.app.config.DevConfig" : DevConfig_AST,
  "ferovinum.app.config.MessageQueueConfig" : MessageQueueConfig_AST,
  "ferovinum.app.config.ClientLogConfig" : ClientLogConfig_AST,
  "ferovinum.app.config.OAuthConfig" : OAuthConfig_AST,
  "ferovinum.app.config.EmailNotificationsConfig" : EmailNotificationsConfig_AST,
  "ferovinum.app.config.RestateConfig" : RestateConfig_AST,
  "ferovinum.app.config.SecretUrls" : SecretUrls_AST,
  "ferovinum.app.config.VisionApiConfig" : VisionApiConfig_AST,
  "ferovinum.app.config.XeroApiConfig" : XeroApiConfig_AST,
  "ferovinum.app.config.TrueLayerApiConfig" : TrueLayerApiConfig_AST,
  "ferovinum.app.config.BlobStoreConfig" : BlobStoreConfig_AST,
  "ferovinum.app.config.S3LocalBlobStoreConfig" : S3LocalBlobStoreConfig_AST,
  "ferovinum.app.config.LocalBlobStoreConfig" : LocalBlobStoreConfig_AST,
  "ferovinum.app.config.S3BlobStoreConfig" : S3BlobStoreConfig_AST,
  "ferovinum.app.config.HotjarConfig" : HotjarConfig_AST,
  "ferovinum.app.config.PurchaseRequestTimelineDurations" : PurchaseRequestTimelineDurations_AST,
  "ferovinum.app.config.AmsterdamWarehouseCompany" : AmsterdamWarehouseCompany_AST,
  "ferovinum.app.config.ReportingStoreConfig" : ReportingStoreConfig_AST,
  "ferovinum.app.config.ApiDeckApiConfig" : ApiDeckApiConfig_AST,
  "ferovinum.app.config.FreightTransportApiConfig" : FreightTransportApiConfig_AST,
  "ferovinum.app.config.ShipEngineApiConfig" : ShipEngineApiConfig_AST,
  "ferovinum.app.config.DocuSignApiConfig" : DocuSignApiConfig_AST,
  "ferovinum.app.config.EmailIntelligenceConfig" : EmailIntelligenceConfig_AST,
  "ferovinum.app.config.ApiConfigName" : ApiConfigName_AST,
  "ferovinum.app.config.ApiConfigValue" : ApiConfigValue_AST,
  "ferovinum.app.config.ApiConfig" : ApiConfig_AST,
  "ferovinum.app.config.NodeServerConfig" : NodeServerConfig_AST,
  "ferovinum.app.config.LogType" : LogType_AST
};
